import React, { useCallback, useMemo, useState } from 'react';
import { ScrollView, StyleSheet, Text, View, Image } from 'react-native';
import { useNavigation, useFocusEffect } from '@react-navigation/core';
import { trpc } from '../../../../../apiCalls/trpcClient';
import { MB_Button } from '@mightybyte/rnw.components.button';
import AntDesign from 'react-native-vector-icons/AntDesign';
import { quarterSharedStyles } from '../quarter/quarterSharedStyles';
import { getColors } from '../../../../../constants/colors';
import { LoadingPage } from '../../../../helperComponents/LoadingPage';
import { Spacer } from '../../../../helperComponents/Spacer';
import { mbTextStyles } from '@mightybyte/rnw.utils.style-utils';
import { textStyles } from '../../../../../constants/textStyles';
import EditIcon from '../../../../../resources/svgComponents/EditIcon';
import { NativeStackNavigationProp } from '@react-navigation/native-stack';
import { AdminScreenProps } from '../../../../../typesAndInterfaces/componentProps';
import { AdminConfirmPopup } from '../quarter/ConfirmActionUI';
import { MB_Modal } from '@mightybyte/rnw.components.modal';
import { utils } from '../../../../../utils/utils';
import { STRING_CONSTANTS } from '../../../../../constants/constants';
import { MiniGameDataForAdmin, MiniGameSetDataForAdmin, MiniGameStatus } from '@sapphicsavvy/business';
import GameControlMenuIcon from '../../../../../resources/svgComponents/Admin/GameControlMenuIcon';
import GradientContainer from '../../../../helperComponents/gradients/GradientContainer';

type Props = {
  addNewButtonAction: () => void;
}

const COLORS = getColors();

const MiniGameCard = ({ game }: { game: MiniGameDataForAdmin }) => {
  let gradientProps: {
    colors: [string, string];
    angle: number;
  } = {
    colors: ['#FCAF4140', '#FCAF4140'],
    angle: -45,
  };
  let textColor = COLORS.orange;
  let iconColor = COLORS.orange;

  if (game.status === MiniGameStatus.active) {
    gradientProps.colors = ['#EE3823', '#FCAF41'];
    // textColor = COLORS.white;
    iconColor = COLORS.white;

  } else if (game.status === MiniGameStatus.past) {
    gradientProps.colors = ['#F35E2E', '#F35E2E'];
    // textColor = COLORS.white;
    iconColor = COLORS.white;

  }
  const hasImage = !!(game.imageUrl?.medium && game.imageUrl.medium.trim().length > 0);

  return (
    <View style={styles.cardWrapper} key={game.id}>
      <GradientContainer {...gradientProps} style={styles.cardContent}>
        {/*<Text style={[styles.statusText, { color: textColor }]}>{status}</Text>*/}
        <View style={styles.imageContainer}>
          {hasImage && game.imageUrl ? (
            <Image
              source={{ uri: game.imageUrl.medium }}
              style={styles.gameImage}
            />
          ) : (
            <GameControlMenuIcon color={iconColor} size={100} />
          )}
        </View>
      </GradientContainer>
      <Spacer height={8} />
      <View style={quarterSharedStyles.rowSpaceBetween}>
        <Text style={[styles.gameTitleText, { color: textColor }]} numberOfLines={2}>
          {game.title}
        </Text>
      </View>
    </View>
  );
};

const MiniGamesList = ({ addNewButtonAction }: Props) => {
  const trpcUtils = trpc.useUtils();
  const navigation = useNavigation<NativeStackNavigationProp<AdminScreenProps<'MiniGames'>>>();
  const [gameToDelete, setGameToDelete] = React.useState<string | undefined>(undefined);
  const [, forceUpdate] = useState(0);

  // Force re-render when screen comes into focus to fix SVG rendering
  useFocusEffect(
    useCallback(() => {
      forceUpdate(n => n + 1);
    }, [])
  );

  const openSet = useCallback((setNumber: number) => {
    navigation.setParams({ setNumber: setNumber.toString() });
  }, [navigation]);

  const { data, isLoading } = trpc.admin.miniGames.getGroupedBySet.useQuery();

  const { mutate: deleteGame } = trpc.admin.miniGames.delete.useMutation();

  const onDeleteConfirm = useCallback(() => {
    if (gameToDelete) {
      deleteGame(
        { id: gameToDelete },
        {
          onSuccess: () => {
            void trpcUtils.admin.miniGames.getGroupedBySet.invalidate();
            utils.showToast('success', 'Mini game deleted');
            setTimeout(() => setGameToDelete(undefined), 600);
          },
          onError: (error) => utils.showPopUp(error.message ?? STRING_CONSTANTS.SOMETHING_WENT_WRONG_PLEASE_TRY_AGAIN),
        }
      );
    }
  }, [deleteGame, gameToDelete, trpcUtils.admin.miniGames.getGroupedBySet]);

  const renderSet = useCallback(({ setData, idx }: { setData: MiniGameSetDataForAdmin; idx: number }) => {
    const setNumber = setData.setNumber;
    const key = `set-${setNumber}-${idx}`;

    // Get date range from the first game in the set (all games in a set share the same dates)
    let setTitle = `Set ${setNumber}`;
    if (setData.games.length > 0) {
      const firstGame = setData.games[0];
      const startDate = new Date(firstGame.startDate);
      const endDate = new Date(firstGame.endDate);
      const startDateStr = `${startDate.getMonth() + 1}/${startDate.getDate()}/${startDate.getFullYear()}`;
      const endDateStr = `${endDate.getMonth() + 1}/${endDate.getDate()}/${endDate.getFullYear()}`;
      setTitle = `Set ${setNumber} (${startDateStr} - ${endDateStr})`;
    }

    return (
      <View style={[quarterSharedStyles.section, { marginBottom: 16 }]} key={key}>
        <View style={styles.setHeader}>
          <Text style={[quarterSharedStyles.titleText, quarterSharedStyles.uppercase]}>
            {setTitle}
          </Text>
          <MB_Button
            title="Edit Mini Games"
            onPress={() => openSet(setNumber)}
            style={[quarterSharedStyles.secondaryBtn, { borderWidth: 1, height: 39 }]}
            textStyle={styles.buttonText}
            leftElement={<EditIcon />}
          />
        </View>
        <View style={styles.setRow}>
          {setData.games.map((game, index: number) => (
            <MiniGameCard key={`${game.id}-${setNumber}-${index}`} game={game} />
          ))}
        </View>
      </View>
    );
  }, [openSet]);

  const gameDeleteNote = useMemo(() => {
    if (gameToDelete) {
      const set = data?.find((setData) => setData.games.some((game) => game.id === gameToDelete));
      const game = set?.games.find((g) => g.id === gameToDelete);
      return game ? `${game.title} (Set ${set?.setNumber})` : undefined;
    }
    return undefined;
  }, [data, gameToDelete]);

  return (
    <View style={{ flex: 1 }}>
      <MB_Modal
        isVisible={!!gameToDelete}
        hideCloseButton={true}
        childrenWrapperStyle={{ padding: 0 }}
      >
        <AdminConfirmPopup
          title={'Delete Mini Game'}
          middle={gameDeleteNote}
          note={'Are you sure you want to delete this mini game? This action cannot be undone.'}
          buttons={[
            <MB_Button
              title={'Delete'}
              onPress={onDeleteConfirm}
              style={[quarterSharedStyles.secondaryBtn, { width: 150, marginRight: 20 }]}
              textStyle={quarterSharedStyles.secondaryBtnText}
            />,
            <MB_Button
              title={'Cancel'}
              onPress={() => setGameToDelete(undefined)}
              style={[quarterSharedStyles.button, { width: 150 }]}
              textStyle={quarterSharedStyles.buttonText}
            />,
          ]}
        />
      </MB_Modal>

      <View style={quarterSharedStyles.rowSpaceBetween}>
        <Text style={quarterSharedStyles.titleText}>Manage Mini Games</Text>
        <MB_Button
          leftElement={<AntDesign name="plus" size={20} color="white" />}
          title="Add New Set"
          onPress={addNewButtonAction}
          style={quarterSharedStyles.button}
          textStyle={quarterSharedStyles.buttonText}
        />
      </View>

      <Spacer height={20} />

      {isLoading ? (
        <View style={{ flex: 1 }}>
          <LoadingPage />
        </View>
      ) : (
        <ScrollView>
          {data && data.length > 0 ? (
            data.map((setData, index) => renderSet({ setData, idx: index }))
          ) : (
            <View style={styles.emptyState}>
              <Text style={styles.emptyStateText}>No mini games yet</Text>
              <Spacer height={10} />
              <Text style={styles.emptyStateSubtext}>Click "Add New Set" to create your first mini game set</Text>
            </View>
          )}
        </ScrollView>
      )}
    </View>
  );
};

export { MiniGamesList };

const styles = StyleSheet.create({
  setRow: {
    flexDirection: 'row',
    justifyContent: 'flex-start',
    alignItems: 'center',
    marginTop: 20,
    flexWrap: 'wrap',
  },
  setHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  cardWrapper: {
    padding: 5,
    borderWidth: 1,
    borderRadius: 5,
    borderColor: COLORS.strokes,
    width: 210,
    height: 183,
    marginRight: 18,
    marginBottom: 18,
  },
  cardContent: {
    padding: 10,
    flex: 1,
    justifyContent: 'space-between',
    borderRadius: 5,
  },
  imageContainer: {
    alignItems: 'center',
    justifyContent: 'center',
    marginBottom: 10,
    width: '100%',
    height: '100%',
  },
  gameImage: {
    width: '100%',
    height: '100%',
  },
  statusText: mbTextStyles([textStyles.smallText, {
    textAlign: 'left',
    textTransform: 'capitalize',
  }]),
  gameTitleText: mbTextStyles([textStyles.largeText, {
    textAlign: 'left',
    fontSize: 18,
    fontWeight: 'bold',
    marginVertical: 5,
  }]),
  smallText: mbTextStyles([textStyles.smallText, {
    textAlign: 'left',
  }]),
  buttonText: mbTextStyles([textStyles.smallText, {
    fontSize: 16,
    color: COLORS.orange,
  }]),
  emptyState: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    paddingVertical: 60,
  },
  emptyStateText: mbTextStyles([textStyles.largeText, {
    color: COLORS.greyText,
    fontSize: 20,
  }]),
  emptyStateSubtext: mbTextStyles([textStyles.normalText, {
    color: COLORS.greyText,
  }]),
});
